import { useState, useRef, useEffect } from 'react';
import { Bell, User, Settings, CreditCard, HelpCircle, LogOut, Calendar, Trophy } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI, customerSubscriptionsAPI } from '../services/api';
import { format, parseISO, isToday, isPast, isFuture } from 'date-fns';
import NextVideoCountdown from './NextVideoCountdown';

export default function CustomerHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  // Fetch user's bookings for notifications
  const { data: bookingsData } = useQuery({
    queryKey: ['my-bookings', user?.id],
    queryFn: () => bookingsAPI.getMyBookings(user?.id),
    enabled: !!user?.id,
    retry: false
  });

  // Fetch subscription info
  const { data: subscriptionData } = useQuery({
    queryKey: ['customer-subscription'],
    queryFn: () => customerSubscriptionsAPI.getSubscription(),
    enabled: !!user?.id,
    retry: false
  });

  const bookings = bookingsData?.data || [];
  const subscription = subscriptionData?.data?.customer;

  // Generate customer-specific notifications
  const notifications = (() => {
    const notifs = [];
    
    // Check for classes today
    const classesToday = bookings.filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const classDate = parseISO(booking.schedule.specificDate);
      return isToday(classDate);
    });

    if (classesToday.length > 0) {
      notifs.push({
        id: 1,
        text: `You have ${classesToday.length} ${classesToday.length === 1 ? 'class' : 'classes'} today!`,
        time: 'Today',
        unread: true,
        icon: Calendar
      });
    }

    // Check for recently completed classes (past 24 hours)
    const recentlyCompleted = bookings.filter(booking => {
      if (!booking.schedule?.specificDate) return false;
      const classDate = parseISO(booking.schedule.specificDate);
      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return isPast(classDate) && !isToday(classDate) && classDate >= yesterday;
    });

    if (recentlyCompleted.length > 0) {
      notifs.push({
        id: 2,
        text: `Great job! You completed ${recentlyCompleted.length} ${recentlyCompleted.length === 1 ? 'class' : 'classes'}! 🎉`,
        time: 'Yesterday',
        unread: true,
        icon: Trophy
      });
    }

    // Subscription status
    if (subscription?.hasPaidSubscription) {
      notifs.push({
        id: 3,
        text: `Your ${subscription.monthlyAmount ? `$${subscription.monthlyAmount}/month` : ''} subscription is active`,
        time: 'Active',
        unread: false,
        icon: CreditCard
      });
    }

    return notifs;
  })();

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasUnreadNotifications = notifications.some(n => n.unread);

  return (
    <header style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-subtle)' }}>
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[var(--text-primary)]">
            Welcome back, {user?.firstName || 'Member'}!
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            {subscription?.hasPaidSubscription 
              ? `Active Subscription • $${parseFloat(subscription.monthlyAmount || 0).toFixed(2)}/month`
              : 'Member'}
          </p>
        </div>

        <div className="flex items-center gap-4">
          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              <Bell className="w-6 h-6 text-[var(--text-secondary)]" />
              {hasUnreadNotifications && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 rounded-lg shadow-lg py-2 z-50" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                <div className="px-4 py-2 border-b border-[var(--border-subtle)]">
                  <h3 className="font-semibold text-[var(--text-primary)]">Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((notif) => {
                      const Icon = notif.icon || Bell;
                      return (
                        <div
                          key={notif.id}
                          className={`px-4 py-3 hover:bg-secondary-50 cursor-pointer ${
                            notif.unread ? 'bg-primary-50' : ''
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              notif.unread ? 'bg-primary-100' : 'bg-secondary-100'
                            }`}>
                              <Icon className={`w-4 h-4 ${notif.unread ? 'text-[var(--text-secondary)]' : 'text-[var(--text-muted)]'}`} />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm text-[var(--text-primary)]">{notif.text}</p>
                              <p className="text-xs text-[var(--text-secondary)] mt-1">{notif.time}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-8 text-center">
                      <Bell className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-2" />
                      <p className="text-sm text-[var(--text-secondary)]">No notifications</p>
                    </div>
                  )}
                </div>
                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-t border-[var(--border-subtle)] text-center">
                    <button className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] font-medium">
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Profile */}
          <div className="relative" ref={profileRef}>
            <div
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-3 p-2 hover:bg-secondary-100 rounded-lg cursor-pointer transition-colors"
            >
              <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[var(--text-secondary)]" />
              </div>
              <div className="text-left">
                <p className="text-sm font-medium text-[var(--text-primary)]">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-[var(--text-secondary)]">Member</p>
              </div>
            </div>

            {/* Profile Dropdown */}
            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg py-2 z-50" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
                <div className="px-4 py-3 border-b border-[var(--border-subtle)]">
                  <p className="font-semibold text-[var(--text-primary)]">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-sm text-[var(--text-secondary)]">{user?.email}</p>
                </div>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/customer/subscription');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-secondary-50 flex items-center gap-3 text-[var(--text-secondary)]"
                >
                  <CreditCard className="w-4 h-4" />
                  <span className="text-sm">Subscription</span>
                </button>
                <button
                  onClick={() => {
                    setShowProfileMenu(false);
                    navigate('/customer/classes');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-secondary-50 flex items-center gap-3 text-[var(--text-secondary)]"
                >
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">My Classes</span>
                </button>
                <div className="border-t border-[var(--border-subtle)] mt-2 pt-2">
                  <button
                    onClick={logout}
                    className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-red-600"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm">Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        </div>
      </div>
      <NextVideoCountdown />
    </header>
  );
}

